import type { MfaChannel } from 'src/queries/Login/types';

export type SendableMfaChannel = Exclude<MfaChannel, 'totp'>;

/** The channels a code can be sent over. An authenticator app's code is never sent. */
export const getSendableMfaChannels = (channels: readonly MfaChannel[]): SendableMfaChannel[] =>
  channels.filter((channel): channel is SendableMfaChannel => channel !== 'totp');

/**
 * The channel the resend screen starts on: the one in use when it is
 * sendable, else the first that is.
 */
export const getInitialResendChannel = (
  current: MfaChannel | undefined,
  available: readonly MfaChannel[]
): SendableMfaChannel | undefined => {
  const sendable = getSendableMfaChannels(available);
  return sendable.find((channel) => channel === current) ?? sendable[0];
};

/** The verify screen's caption for where the code came from. */
export const getVerifyMfaCaption = ({
  channel,
  destination,
  isMfaCodeSent,
}: {
  channel: MfaChannel;
  destination: string;
  isMfaCodeSent: boolean;
}): string => {
  if (channel === 'totp') return 'Enter the 6-digit code from your authenticator app.';
  if (!isMfaCodeSent) return "We couldn't send your code. Choose Resend code to try again.";
  return `We sent a one-time code to: ${destination}`;
};

/** A TOTP secret in groups of four, easier to read and type into an authenticator app. */
export const formatTotpSecret = (secret: string): string => secret.match(/.{1,4}/g)?.join(' ') ?? secret;
